import { useMemo, useState } from 'react';
import { type MassTable, molarMass, molarity, prettyFormula } from '@chemai/engine';
import { type MessageKey, useI18n } from '../../i18n';
import { NumberField, SaveButton, Segmented, SelectField, SimBadge, Stat } from '../../components/ui';
import { progress } from '../../lib/progress';

interface Solute { formula: string; rgb: [number, number, number] | null; hazard: MessageKey; colourPerMolar: number }

const SOLUTES: Solute[] = [
  { formula: 'NaCl', rgb: null, hazard: 'conc.hazard.none', colourPerMolar: 0 },
  { formula: 'NaOH', rgb: null, hazard: 'conc.hazard.NaOH', colourPerMolar: 0 },
  { formula: 'C6H12O6', rgb: null, hazard: 'conc.hazard.none', colourPerMolar: 0 },
  { formula: 'CuSO4·5H2O', rgb: [30, 110, 220], hazard: 'conc.hazard.CuSO4', colourPerMolar: 4 },
  { formula: 'KMnO4', rgb: [140, 20, 120], hazard: 'conc.hazard.KMnO4', colourPerMolar: 60 },
];

export function ConcentrationSim() {
  const { t, num, errorMessage } = useI18n();
  const [choice, setChoice] = useState('CuSO4·5H2O');
  const [custom, setCustom] = useState('KNO3');
  const [mass, setMass] = useState(6.25);
  const [volume, setVolume] = useState<'50' | '100' | '250' | '500'>('250');
  const [table, setTable] = useState<MassTable>('iupac');

  const solute = SOLUTES.find((s) => s.formula === choice);
  const formula = choice === 'custom' ? custom : choice;

  const result = useMemo(() => {
    try {
      const mm = molarMass(formula, table);
      const r = molarity({ mass, molarMass: mm.value, volume: Number(volume) });
      return { mm, r, error: null as string | null };
    } catch (e) {
      return { mm: null, r: null, error: errorMessage(e) };
    }
  }, [formula, table, mass, volume, errorMessage]);

  const alpha = solute?.rgb && result.r ? Math.min(0.92, result.r.molarity * solute.colourPerMolar * 0.5) : 0;
  const colour = solute?.rgb ? `rgba(${solute.rgb.join(',')}, ${alpha.toFixed(3)})` : 'transparent';
  const pf = prettyFormula(formula);

  return (
    <div className="sim">
      <SimBadge />
      <div className="sim-grid">
        <section className="panel">
          <h3>{t('tit.setup')}</h3>
          <SelectField
            label={t('conc.solute')}
            value={choice}
            onChange={setChoice}
            options={[...SOLUTES.map((s) => ({ value: s.formula, label: prettyFormula(s.formula) })), { value: 'custom', label: t('conc.custom') }]}
          />
          {choice === 'custom' && (
            <div className="field">
              <label htmlFor="conc-formula">{t('conc.formula')}</label>
              <input id="conc-formula" value={custom} onChange={(e) => setCustom(e.target.value)} spellCheck={false} autoCapitalize="off" />
            </div>
          )}
          <NumberField label={t('conc.mass')} value={mass} onChange={setMass} min={0} max={100} step={0.05} slider />
          <Segmented label={t('conc.flask')} value={volume} onChange={setVolume} options={['50', '100', '250', '500'].map((v) => ({ value: v as typeof volume, label: `${v} mL` }))} />
          <Segmented label={t('conc.masses')} value={table} onChange={setTable} options={[{ value: 'iupac', label: t('conc.masses.iupac') }, { value: 'bac', label: t('conc.masses.bac') }]} />
          <p className="warn-soft">⚠ {t(solute?.hazard ?? 'conc.hazard.none')}</p>
        </section>

        <section className="panel apparatus-panel">
          <h3>{t('lab.equipment')}</h3>
          <svg viewBox="0 0 200 300" className="apparatus" role="img" aria-label={`${pf}, ${volume} mL`}>
            <defs>
              <clipPath id="vf-clip"><path d="M90 20 L90 150 Q40 165 40 220 Q40 285 100 285 Q160 285 160 220 Q160 165 110 150 L110 20 Z" /></clipPath>
            </defs>
            <g clipPath="url(#vf-clip)">
              <rect x="0" y="70" width="200" height="230" className="water" />
              <rect x="0" y="70" width="200" height="230" fill={colour} />
            </g>
            <path d="M90 20 L90 150 Q40 165 40 220 Q40 285 100 285 Q160 285 160 220 Q160 165 110 150 L110 20" fill="none" className="flask-outline" />
            <line x1="86" x2="114" y1="70" y2="70" className="grad" />
            <text x="118" y="73" className="grad-text">{volume} mL</text>
            <text x="100" y="235" textAnchor="middle" className="flask-label">{pf}</text>
          </svg>
          {solute?.rgb && <p className="muted small">{t('conc.colourNote')}</p>}
        </section>

        <section className="panel readouts" aria-live="polite">
          <h3>{t('lab.calculations')}</h3>
          {result.error && <p className="error" role="alert">{result.error}</p>}
          {result.mm && result.r && (
            <>
              <div className="stats">
                <Stat label={t('conc.molarity')} value={num(result.r.molarity, 4)} unit="mol/L" emphasis />
                <Stat label={t('conc.molarMass')} value={num(result.mm.value, 3)} unit="g/mol" />
                <Stat label={t('conc.moles')} value={num(result.r.moles, 5)} unit="mol" />
                <Stat label={t('conc.massConc')} value={num(result.r.massConcentration, 3)} unit="g/L" />
              </div>
              <ul className="calc">
                <li><code>M({pf}) = {result.mm.terms.map((x) => `${x.count > 1 ? `${x.count}·` : ''}${num(x.atomicMass, 4)}`).join(' + ')} = {num(result.mm.value, 3)} g/mol</code></li>
                <li><code>n = m / M = {num(mass, 3)} g / {num(result.mm.value, 3)} g/mol = {num(result.r.moles, 5)} mol</code></li>
                <li><code>c = n / V = {num(result.r.moles, 5)} mol / {num(Number(volume) / 1000, 3)} L = {num(result.r.molarity, 4)} mol/L</code></li>
              </ul>
              <SaveButton onSave={() => progress.recordExperiment('concentration', { m: mass, f: formula, v: Number(volume), c: Math.round(result.r!.molarity * 10000) / 10000 })} />
            </>
          )}
        </section>
      </div>
      <section className="panel">
        <h3>{t('lab.steps')}</h3>
        <ol className="steps">
          <li>{t('conc.step1', { m: num(mass, 2), f: pf })}</li>
          <li>{t('conc.step2')}</li>
          <li>{t('conc.step3', { v: volume })}</li>
          <li>{t('conc.step4')}</li>
        </ol>
      </section>
    </div>
  );
}
